import { Camera, ChevronLeft, Lock, MapPin, PencilLine, ShoppingCart } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { geoDistance } from "base/ui/location";
import { Popup } from "base/ui/popups";
import { useBroadcastedState, useLocalStorageState } from "base/utils/events";

import ShopPhotoCapture from "./ShopPhotoCapture";
import { sendStaffLocation } from "../../../../useStaffLocation";
import ResponsivePopup from "../../../ui/ResponsivePopup";

const CHECKIN_RANGE_KMS = 0.1;
const VISIT_Z_INDEX = 35; // above route plan popup & chat windows (30), below toasts (40)

const CUSTOMER_VISIT_STATUS = {
	CHECK_IN: "check_in",
	CHECK_OUT: "check_out",
	SHOP_PHOTO: "shop_photo",
};

function useActiveCustomerVisit() {
	const [active_visit, setActiveVisit] = useLocalStorageState("staff_customer_visit");
	return [active_visit?.customer_id ? active_visit : null, setActiveVisit];
}

const pad = (n) => String(n).padStart(2, "0");

function formatTimer(ms) {
	const secs = Math.max(0, Math.floor(ms / 1000));
	const h = Math.floor(secs / 3600);
	const m = Math.floor((secs % 3600) / 60);
	const s = secs % 60;
	return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function formatDuration(ms) {
	const secs = Math.max(0, Math.floor(ms / 1000));
	const h = Math.floor(secs / 3600);
	const m = Math.floor((secs % 3600) / 60);
	return h ? `${h} h ${m} min` : `${m} min ${secs % 60} s`;
}

function formatTimeOfDay(millis) {
	return new Date(millis).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDistance(kms) {
	return kms < 1 ? `${Math.round(kms * 1000)} m` : `${kms.toFixed(1)} km`;
}

function formatPhone(phone_number) {
	if (!phone_number) return "";
	return phone_number.startsWith("+") ? phone_number : `+${phone_number}`;
}

/* gps_logs are sorted latest first */
function getVisitRecords(gps_logs, customer_id, since) {
	const records = { order_ids: [], notes_count: 0 };
	(gps_logs || []).forEach((log) => {
		const info = log.data?.info;
		if (info?.buying_for_user_id !== customer_id || log.created_at < since) return;
		if (info.order_id && !records.order_ids.includes(info.order_id)) {
			records.order_ids.push(info.order_id);
		}
		if (info.is_customer_notes) {
			records.notes_count += 1;
		}
	});
	return records;
}

function getRecordsSummary(records) {
	const summary = [];
	if (records.order_ids.length) {
		summary.push(records.order_ids.length > 1 ? `Orders (${records.order_ids.length})` : "Order");
	}
	if (records.notes_count) {
		summary.push("Notes");
	}
	return summary.join(", ") || "Nothing";
}

function useDistanceFromCustomer(customer) {
	const [distance, setDistance] = useState({ is_loading: !!customer.lat_long?.length });

	useEffect(() => {
		if (!customer.lat_long?.length || !navigator.geolocation) return;
		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords;
				setDistance({ kms: geoDistance(latitude, longitude, ...customer.lat_long, "K") });
			},
			() => setDistance({}),
			{ enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
		);
	}, [customer._id]);

	return distance;
}

function DistanceBanner({ customer }) {
	const { is_loading, kms } = useDistanceFromCustomer(customer);
	if (is_loading) {
		return (
			<div className="tw-flex tw-items-center tw-gap-2 tw-mt-3 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-gray-100 tw-text-gray-500 tw-text-sm">
				<MapPin size={16} />
				Checking your distance from this shop...
			</div>
		);
	}
	if (kms === undefined) return null;
	const is_in_range = kms <= CHECKIN_RANGE_KMS;
	return (
		<div
			className={`tw-flex tw-items-center tw-gap-2 tw-mt-3 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-font-semibold ${is_in_range ? "tw-bg-green-50 tw-text-green-800" : "tw-bg-amber-50 tw-text-amber-800"}`}
		>
			<MapPin size={16} />
			{is_in_range
				? `You're ${formatDistance(kms)} from this shop · within check-in range`
				: `You're ${formatDistance(kms)} from this shop`}
		</div>
	);
}

function VisitActionTile({ icon: Icon, label, is_locked, onClick }) {
	if (is_locked) {
		return (
			<div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-6 tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-bg-gray-100 tw-text-gray-500 tw-font-semibold">
				<Lock size={20} />
				{label}
			</div>
		);
	}
	return (
		<button
			className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-6 tw-rounded-xl tw-border tw-border-green-700 tw-bg-white tw-text-green-800 tw-font-semibold"
			onClick={onClick}
			type="button"
		>
			<Icon size={20} />
			{label}
		</button>
	);
}

function CheckoutConfirmSheet({ customer, checkin_at, records, is_saving, onStay, onConfirm }) {
	return (
		<div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-end tw-bg-black tw-bg-opacity-40">
			<div className="tw-w-full tw-bg-white tw-rounded-t-2xl tw-p-5">
				<div className="tw-text-lg tw-font-bold tw-mb-4">Check out of {customer.name}?</div>
				<div className="tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-8 tw-gap-y-2 tw-text-sm tw-mb-6">
					<span className="tw-text-gray-500">Checked in</span>
					<span className="tw-font-bold">{formatTimeOfDay(checkin_at)}</span>
					<span className="tw-text-gray-500">Time in store</span>
					<span className="tw-font-bold">{formatDuration(Date.now() - checkin_at)}</span>
					<span className="tw-text-gray-500">Recorded</span>
					<span className="tw-font-bold">{getRecordsSummary(records)}</span>
				</div>
				<div className="tw-grid tw-grid-cols-[2fr_3fr] tw-gap-3">
					<button
						className="tw-py-3 tw-rounded-xl tw-border tw-border-gray-300 tw-font-bold"
						onClick={onStay}
						type="button"
					>
						Stay
					</button>
					<button
						className="tw-py-3 tw-rounded-xl tw-bg-yellow-500 tw-font-bold disabled:tw-opacity-60"
						disabled={is_saving}
						onClick={onConfirm}
						type="button"
					>
						{is_saving ? "Checking out..." : "Confirm check-out"}
					</button>
				</div>
			</div>
		</div>
	);
}

function CustomerVisitView({
	delivery_route_id, customer, stop_number, total_stops, gps_logs, require_shop_photo,
	onClose, onTakeOrder, onAddNote,
}) {
	const [active_visit, setActiveVisit] = useActiveCustomerVisit();
	const [now, setNow] = useState(Date.now());
	const [is_saving, setSaving] = useState(false);
	const [show_checkout_confirm, setShowCheckoutConfirm] = useState(false);
	const [show_camera, setShowCamera] = useState(false);

	const is_checked_in = active_visit?.customer_id === customer._id && active_visit?.delivery_route_id === delivery_route_id;
	const other_active_visit = active_visit && !is_checked_in ? active_visit : null;
	const stop_text = stop_number && total_stops ? `Stop ${stop_number} of ${total_stops}` : "";

	/* prefer server time of the check-in log so that order/note logs are compared on the same clock */
	const checkin_log = is_checked_in && gps_logs?.find(
		(log) => log.data?.info?.buying_for_user_id === customer._id
			&& log.data.info.customer_visit === CUSTOMER_VISIT_STATUS.CHECK_IN,
	);
	const visit_started_at = checkin_log && Math.abs(checkin_log.created_at - active_visit.checkin_at) < 5 * 60 * 1000
		? checkin_log.created_at
		: active_visit?.checkin_at;
	const records = is_checked_in ? getVisitRecords(gps_logs, customer._id, visit_started_at) : null;
	const has_records = !!records && (records.order_ids.length > 0 || records.notes_count > 0);

	useEffect(() => {
		if (!is_checked_in) return undefined;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [is_checked_in]);

	const logVisit = async (customer_visit, extra_info) => {
		setSaving(true);
		try {
			const logged = await sendStaffLocation(
				delivery_route_id,
				{ buying_for_user_id: customer._id, customer_visit, ...extra_info },
				{ show_alert: true },
			);
			if (!logged) {
				Popup.toast(
					<div className="tw-rounded tw-p-2 tw-text-sm">Couldn&apos;t capture your location. Please try again.</div>,
					3000,
				);
			}
			return logged;
		} finally {
			setSaving(false);
		}
	};

	const doCheckIn = async (shop_photo_url) => {
		if (await logVisit(CUSTOMER_VISIT_STATUS.CHECK_IN, { shop_photo_url })) {
			setActiveVisit({
				delivery_route_id,
				customer_id: customer._id,
				customer_name: customer.name,
				checkin_at: Date.now(),
				shop_photo_url,
			});
			setShowCamera(false);
		}
	};

	const checkIn = () => {
		if (is_saving || other_active_visit) return;
		if (require_shop_photo) {
			setShowCamera(true);
			return;
		}
		doCheckIn();
	};

	/* logged as a separate entry so the original check-in record stays untouched, export picks the latest photo */
	const replaceShopPhoto = async (shop_photo_url) => {
		if (await logVisit(CUSTOMER_VISIT_STATUS.SHOP_PHOTO, { shop_photo_url, checkin_at: active_visit.checkin_at })) {
			setActiveVisit({ ...active_visit, shop_photo_url });
			setShowCamera(false);
		}
	};

	const checkOut = async () => {
		if (is_saving) return;
		if (await logVisit(CUSTOMER_VISIT_STATUS.CHECK_OUT, { checkin_at: active_visit.checkin_at })) {
			setActiveVisit(null);
			setShowCheckoutConfirm(false);
			onClose();
		}
	};

	return (
		<div className="tw-relative tw-flex tw-flex-col tw-h-full tw-bg-gray-100">
			{is_checked_in ? (
				<div className="tw-bg-green-700 tw-text-white tw-px-4 tw-pt-3 tw-pb-4">
					<div className="tw-flex tw-items-start tw-gap-2">
						<ChevronLeft size={22} className="tw-cursor-pointer tw-mt-0.5" onClick={onClose} />
						<div>
							<div className="tw-text-xs tw-opacity-90">
								Checked in at {formatTimeOfDay(active_visit.checkin_at)}{stop_text ? ` · ${stop_text}` : ""}
							</div>
							<div className="tw-text-lg tw-font-bold">{customer.name}</div>
							<div className="tw-flex tw-items-baseline tw-gap-2 tw-mt-1">
								<span className="tw-text-4xl tw-font-bold">{formatTimer(now - active_visit.checkin_at)}</span>
								<span className="tw-text-sm">in store</span>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="tw-flex tw-items-center tw-gap-3 tw-bg-white tw-border-b tw-px-4 tw-py-3">
					<ChevronLeft size={22} className="tw-cursor-pointer" onClick={onClose} />
					<div>
						{stop_text ? <div className="tw-text-xs tw-text-gray-500">{stop_text}</div> : null}
						<div className="tw-text-lg tw-font-bold">{customer.name}</div>
					</div>
				</div>
			)}

			<div className="tw-grow tw-overflow-y-auto tw-p-4">
				{!is_checked_in ? (
					<div className="tw-bg-white tw-rounded-xl tw-border tw-p-4 tw-mb-6">
						<div className="tw-text-sm tw-text-gray-600">
							{customer.name}{customer.phone_number ? ` · ${formatPhone(customer.phone_number)}` : ""}
						</div>
						<DistanceBanner customer={customer} />
						{other_active_visit ? (
							<div className="tw-mt-3 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-amber-50 tw-text-amber-800 tw-text-sm">
								You&apos;re checked in at <b>{other_active_visit.customer_name}</b>. Check out there before checking in here.
							</div>
						) : null}
					</div>
				) : null}

				{!is_checked_in ? (
					<div className="tw-text-xs tw-font-bold tw-tracking-wide tw-text-gray-500 tw-mb-2">DURING THE VISIT</div>
				) : null}
				<div className="tw-grid tw-grid-cols-2 tw-gap-3">
					<VisitActionTile icon={ShoppingCart} label="Take order" is_locked={!is_checked_in} onClick={() => onTakeOrder(customer)} />
					<VisitActionTile icon={PencilLine} label="Add note" is_locked={!is_checked_in} onClick={() => onAddNote(customer)} />
				</div>

				{is_checked_in ? (
					<>
						<div className="tw-text-xs tw-font-bold tw-tracking-wide tw-text-gray-500 tw-mt-6 tw-mb-2">RECORDED THIS VISIT</div>
						{active_visit.shop_photo_url || require_shop_photo ? (
							<div className="tw-flex tw-items-center tw-gap-3 tw-bg-white tw-rounded-xl tw-border tw-px-4 tw-py-2 tw-mb-2 tw-text-sm">
								{active_visit.shop_photo_url ? (
									<a className="tw-flex tw-items-center tw-gap-3 tw-grow" href={active_visit.shop_photo_url} rel="noreferrer" target="_blank">
										<img src={active_visit.shop_photo_url} alt="Shop" className="tw-w-10 tw-h-10 tw-rounded tw-object-cover" />
										Shop photo
									</a>
								) : (
									<span className="tw-grow tw-text-gray-500">No shop photo yet</span>
								)}
								<button
									className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-green-700 tw-text-green-800 tw-font-semibold tw-text-xs disabled:tw-opacity-50"
									disabled={is_saving}
									onClick={() => setShowCamera(true)}
									type="button"
								>
									<Camera size={14} />
									{active_visit.shop_photo_url ? "Take new photo" : "Take photo"}
								</button>
							</div>
						) : null}
						{has_records ? (
							<div className="tw-bg-white tw-rounded-xl tw-border tw-divide-y tw-text-sm">
								{records.order_ids.map((order_id) => (
									<div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3" key={order_id}>
										<ShoppingCart size={16} className="tw-text-green-700" />
										Order placed
										<span className="tw-ml-auto tw-text-xs tw-text-gray-400">#{order_id.slice(-6)}</span>
									</div>
								))}
								{records.notes_count ? (
									<div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3">
										<PencilLine size={16} className="tw-text-green-700" />
										Notes added
									</div>
								) : null}
							</div>
						) : (
							<div className="tw-py-4 tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-text-center tw-text-sm tw-text-gray-500">
								Nothing recorded yet
							</div>
						)}
					</>
				) : (
					<div className="tw-text-center tw-text-sm tw-text-gray-600 tw-mt-3">Check in to take an order or add a note.</div>
				)}
			</div>

			<div className="tw-bg-white tw-border-t tw-px-4 tw-pt-3 tw-pb-4">
				{is_checked_in ? (
					<>
						<button
							className={`tw-w-full tw-py-3 tw-rounded-xl tw-font-bold ${has_records ? "tw-bg-yellow-500" : "tw-bg-gray-200 tw-text-gray-500"}`}
							disabled={!has_records || is_saving}
							onClick={() => setShowCheckoutConfirm(true)}
							type="button"
						>
							Check out
						</button>
						<div className="tw-text-center tw-text-xs tw-text-gray-500 tw-mt-2">
							{has_records ? "Records the time and your GPS location" : "Take an order or add a note before checking out"}
						</div>
					</>
				) : (
					<button
						className="tw-w-full tw-py-3 tw-rounded-xl tw-font-bold tw-bg-yellow-500 disabled:tw-bg-gray-200 disabled:tw-text-gray-500"
						disabled={is_saving || !!other_active_visit}
						onClick={checkIn}
						type="button"
					>
						{is_saving ? "Checking in..." : require_shop_photo ? "Take shop photo & check in" : "Check in"}
					</button>
				)}
			</div>

			{show_camera ? (
				<ShopPhotoCapture
					customer={customer}
					is_replace={is_checked_in}
					onCancel={() => setShowCamera(false)}
					onPhotoUploaded={is_checked_in ? replaceShopPhoto : doCheckIn}
				/>
			) : null}

			{show_checkout_confirm && is_checked_in ? (
				<CheckoutConfirmSheet
					customer={customer}
					checkin_at={active_visit.checkin_at}
					records={records}
					is_saving={is_saving}
					onStay={() => setShowCheckoutConfirm(false)}
					onConfirm={checkOut}
				/>
			) : null}
		</div>
	);
}

/* Full page on mobile, popup on larger screens */
function CustomerVisit(props) {
	const { onClose } = props;
	const [screen] = useBroadcastedState("tw_screen_size");

	useEffect(() => {
		if (screen.md) return undefined;
		window.addEventListener("popstate", onClose, { once: true });
		return () => window.removeEventListener("popstate", onClose);
	}, [screen.md]);

	if (screen.md) {
		return (
			<ResponsivePopup show={true} onClose={onClose} titleRenderer={() => null}
				style_props={{ z_index: VISIT_Z_INDEX, md_max_width: "480px" }}
			>
				<div className="tw-h-[70vh]">
					<CustomerVisitView {...props} />
				</div>
			</ResponsivePopup>
		);
	}
	return createPortal(
		<div className="tw-fixed tw-inset-0" style={{ zIndex: VISIT_Z_INDEX }}>
			<CustomerVisitView {...props} />
		</div>,
		document.body,
	);
}

export { CUSTOMER_VISIT_STATUS, useActiveCustomerVisit };
export default CustomerVisit;
